/* CoreSectionRouterComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./CoreSectionRouterRenderer");
const ModalUserFeedback = require("@gluenetworks/steve_ui_widgets").UserFeedback.ModalUserFeedback;
const NOTHING_HAPPENED_RESULTS = {
    status: "SUCCESS",
    success: true,
    swapResults: [],
};

/**
 * @typedef RouteInfo
 * @type {Object}
 * @property {SectionComponent} section - The Section Mgr instance associated with this route.
 * @property {RouteDetails} routeDetails - The current route details for this section.
 */
/**
 * @typedef SectionDetails
 * @type {Object}
 * @property {SteveComponent} fullyQualifiedSectionName - The fully qualified name of the Section component.
 * @property {SteveComponent} cmpnt - The Section component that this SectionDetails represents.
 * @property {SteveDependencyInjector} di - The child di created for this Section.
 */
/**
 * @typedef SectionSwapDetails
 * @type {object}
 * @property {SectionDetails} newSectionDetails - The details about the section we just swapped to.
 * @property {SectionDetails} oldSectionDetails - The details about the section we just swapped from.
 *
 */
/**
 * @event SectionSwapped
 * @param {SectionDetails} newSectionDetails - The details about the section we just swapped to.
 * @param {SectionDetails} oldSectionDetails - The details about the section we just swapped from.
 */
/**
 * @event SectionChangeCompleted
 * @param {Array.<SectionSwapDetails>} results - This is an array of of SectionSwapDetails objects due to the
 * possibility that multiple section change requests can be received in quick succession. In these cases each request
 * will be handled in order and the results for all will be included in this event.
 */
/**
 * @event SectionChangeFailed
 * @param {Error} error - The rejected error that caused the Section Change Failure.
 */
/**
 * @event ShuttingDown
 * @description Fired when this Section Router has started its shutdown process.
 */

/**
 * @event ShutdownComplete
 * @description Fired when this Section Router has been completely shutdown.
 */
/**
 * This is the Core Router, it is responsible for abstracting away the routing logic both handling the url changes
 * in the browser and the presentation of Apps and Sections.
 * @fires CoreRouterComponent#SectionSwapped
 * @fires CoreRouterComponent#SectionChangeCompleted
 * @fires CoreRouterComponent#SectionChangeFailed
 * @fires CoreRouterComponent#ShuttingDown
 * @fires CoreRouterComponent#ShutdownComplete
 */
class CoreSectionRouterComponent extends EventEmitter {
    constructor(steveCore, logger, di, router) {
        super();
        this._steve = steveCore;
        this._logger = logger;
        this._router = router;
        this._di = di;
        this._di.registry.register("$teve.sectionRouter", "SectionRouter", this);
        this._sectionRouterDi = this._di.createDependencyInjector();

        this._userFeedbackComponent = new ModalUserFeedback();
        this._sectionRouterDi.registry.register(
            "$teve.ui.userFeedback",
            this._userFeedbackComponent
        );

        this._routeChangesInProgressPromise = undefined;
        this._routeChangesInProgress = [];

        /**
         * The current section details.
         * @type {SectionDetails}
         */
        this._currentSectionDetails = undefined;
        /**
         * When in the middle of swapping sections the section we are swapping to.
         * @type {SectionDetails}
         */
        this._nextSectionDetails = undefined;

        this._renderer = new Renderer(this, logger);

        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */

    /*******************************************/
    _registerListeners() {
        this._steve.on(
            "SteveStartupCompleted",
            (this._onSteveStartupCompleted = this._onSteveStartupCompleted.bind(this))
        );
        this._router.onPathTokenRollupChange(
            ["app", "s"],
            (this._onRouteInfoChanged = this._onRouteInfoChanged.bind(this))
        );
        this._router.on(
            "RouteRefreshed",
            (this._onRouteRefreshed = this._onRouteRefreshed.bind(this))
        );
    }

    _unregisterListeners() {
        this._steve.removeListener("SteveStartupCompleted", this._onSteveStartupCompleted);
        this._router.removePathTokenRollupChangeListener(["app", "s"], this._onRouteInfoChanged);
        this._router.removeListener("RouteRefreshed", this._onRouteRefreshed);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */

    /*******************************************/
    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        this.emit("ShuttingDown");
        this._shutdownInProgress = this.whenRouteChangesComplete().then(() => {
            return this._renderer.shutdown().then(() => {
                this._renderer = undefined;

                //clean up any pollution in the parent injector
                this._di.registry.unregister("$teve.sectionRouter", "SectionRouter");
                this._di = undefined;

                //Cleanup section router DI
                this._sectionRouterDi.shutdown();
                this._sectionRouterDi = undefined;
                this._userFeedbackComponent = undefined; //this is shutdown in the _sectionRouterDi shutdown()

                this._logger = undefined;
                this._router = undefined;

                this._routeChangesInProgressPromise = undefined;
                this._routeChangesInProgress = undefined;
                this._shutdownInProgress = undefined;
                this.emit("ShutdownComplete");
            });
        });
        return this._shutdownInProgress;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */

    /*******************************************/
    _getSectionDetailsFromFullyQualifiedSectionName(fqsn) {
        let matches = fqsn.match(/App\.(.+)\.Section\.(.+)/);
        return {
            appName: matches[1],
            sectionName: matches[2],
        };
    }

    _getFullyQualifiedSectionName(appName, sectionName) {
        let fullyQualifiedSectionName = `App.${appName}.Section.${sectionName}`;
        return fullyQualifiedSectionName;
    }

    _getFqsnFromRouteDetails(routeDetails) {
        let appName = _.get(routeDetails, "app");
        let sectionName = _.get(routeDetails, "s");
        if (appName && sectionName) {
            return this._getFullyQualifiedSectionName(appName, sectionName);
        } else {
            return null;
        }
    }

    _swapSection(fullyQualifiedSectionName) {
        this._logger.debug(
            {fullyQualifiedSectionName: fullyQualifiedSectionName},
            `New Swap Section has started (${fullyQualifiedSectionName})`
        );
        let swapProm = new Promise((resolve, reject) => {
            let newDi = this._sectionRouterDi.createDependencyInjector();
            try {
                let nextSection = newDi.createInstance(fullyQualifiedSectionName);

                this._nextSectionDetails = {
                    fullyQualifiedSectionName: fullyQualifiedSectionName,
                    cmpnt: nextSection,
                    di: newDi,
                };
                //this doesn't reject only ever resolves...
                let loaderProm = this._userFeedbackComponent.displayLoading();
                loaderProm
                    .then(() => {
                        return this._renderer.render();
                    })
                    .then(() => {
                        //If the render completed then nextSection is now the new section
                        let oldSectionDeets = this._currentSectionDetails;
                        this._currentSectionDetails = this._nextSectionDetails;
                        this._nextSectionDetails = undefined;
                        //Hide/Resolve this only after the loading mask has been completely presented

                        this.emit("SectionSwapped", this._currentSectionDetails, oldSectionDeets);
                        resolve({
                            newSectionDetails: this._currentSectionDetails,
                            oldSectionDetails: oldSectionDeets,
                        });
                    })
                    .catch((rendererErr) => {
                        newDi.shutdown();
                        this._currentSectionDetails = undefined;
                        this._nextSectionDetails = undefined;
                        this._logger.warn(
                            {
                                error: rendererErr,
                                fullyQualifiedSectionName: fullyQualifiedSectionName,
                            },
                            `Renderer failed when trying to load section (${fullyQualifiedSectionName}).`
                        );
                        //Reject this only after the loading mask has been completely presented

                        reject(rendererErr);
                    });
            } catch (e) {
                newDi.shutdown();
                this._logger.warn(
                    {error: e, fullyQualifiedSectionName: fullyQualifiedSectionName},
                    `There is no Section registered under the fully qualified name ${fullyQualifiedSectionName}.`
                );
                let sectDeets =
                    this._getSectionDetailsFromFullyQualifiedSectionName(fullyQualifiedSectionName);
                reject({
                    errorCode: "SectionNotFound",
                    errorTitle: `Section Could Not Be Found`,
                    errorMsg: `Cannot take you to the "${sectDeets.sectionName}" Section. A Section must be registered using the fully qualified name "${fullyQualifiedSectionName}".`,
                });
            }
        });
        return swapProm;
    }

    /**
     * This method changes from one Section to the next. It does this in a First Come basis. This creates a FIFO using
     * some Promise/Closure Trickery, starvation can occur and that IS acceptable.
     *
     * @param {string} fullyQualifiedSectionName - The FQN of the section to route to.
     * @returns {Promise} A Promise that will ALWAYS be resolved with the details of the swap.
     * @private
     */
    _changeSection(fullyQualifiedSectionName) {
        if (!fullyQualifiedSectionName) {
            return Promise.resolve({
                status: "FAIL",
                fail: true,
                swapResults: [
                    {
                        errorCode: "SectionNotFound",
                        errorTitle: `Section Could Not Be Found`,
                        errorMsg: `Could not find a Section in the current url.`,
                    },
                ],
            });
        }
        //Starts the swap when the previous swaps have finished (doesn't care if they succeeded or not)
        //swapProm will resolve or reject only after all previous swaps have either resolved or rejected.
        let swapNoMatterWhat = () => {
            return this._swapSection(fullyQualifiedSectionName);
        };
        let swapProm = Promise.resolve(this._routeChangesInProgressPromise).then(
            (/*rslts*/) => {
                return swapNoMatterWhat();
            },
            (/*e*/) => {
                return swapNoMatterWhat();
            }
        );

        //Add the result of this new swap to the route change promises array, this will be the result returned at the end of this chain.
        this._routeChangesInProgress.push(swapProm);
        //create a local copy of the swaps that came before and included this swap.
        let swapsInProgress = [...this._routeChangesInProgress];

        //Set the "Most Recent" swap promise in the class level scope
        //swap Prom is closed over so we upon completion can use strict equality to see if we were the last ones to request a swap.
        this._routeChangesInProgressPromise = swapProm;

        //Used to provide a single promise that always resolves with the full details of all the swaps that took place.
        let getTheResultsOfThisSwapAndAllBeforeIt = () => {
            return Promise.all(swapsInProgress).then(
                () => {
                    return Promise.resolve({
                        status: "SUCCESS",
                        success: true,
                        swapResults: swapsInProgress,
                    });
                },
                () => {
                    return Promise.resolve({
                        status: "FAIL",
                        fail: true,
                        swapResults: swapsInProgress,
                    });
                }
            );
        };

        //When this promise completes/fails check and see if another route change has happened since.
        return swapProm.then(
            (results) => {
                let loaderHiddenProm = Promise.resolve();
                //If swapProm is the same as _routeChangesInProgressPromise then we were the last swap to occur so lets wrap it up.
                if (this._routeChangesInProgressPromise === swapProm) {
                    this._routeChangesInProgressPromise = undefined;
                    this._routeChangesInProgress = [];
                    //Dont tell anyone we are swapped until the loader is closed
                    loaderHiddenProm = this._userFeedbackComponent.hideDisplayed().then(() => {
                        //This event is used by the whenRouteChangesComplete() to truly know when a section change storm has finished
                        this.emit("SectionChangeCompleted", swapsInProgress);
                        this._logger.debug(
                            {
                                fullyQualifiedSectionName: fullyQualifiedSectionName,
                                results: results,
                                swapsInProgress: swapsInProgress,
                            },
                            `Swap Section completed and this is the last buffered swap (${fullyQualifiedSectionName})`
                        );
                    });
                } else {
                    this._logger.debug(
                        {
                            fullyQualifiedSectionName: fullyQualifiedSectionName,
                            swapsInProgress: swapsInProgress,
                        },
                        `Swap Section completed but others started before this one finished (${fullyQualifiedSectionName})`
                    );
                }
                //Either way (Success or Failure) let someone know that this particular swap and all the swaps before it have completed, with enough info to know which ones succeeded or failed.
                return loaderHiddenProm.then(getTheResultsOfThisSwapAndAllBeforeIt);
            },
            (error) => {
                //IF swapProm is the same as _routeChangesInProgressPromise then we were the last swap to occur so lets wrap it up.
                if (this._routeChangesInProgressPromise === swapProm) {
                    this._routeChangesInProgressPromise = undefined;
                    this._routeChangesInProgress = [];
                    //This event is used by the whenRouteChangesComplete() to truly know when a section change storm has finished
                    this.emit("SectionChangeCompleted", swapsInProgress);
                    this._logger.debug(
                        {
                            fullyQualifiedSectionName: fullyQualifiedSectionName,
                            swapsInProgress: swapsInProgress,
                        },
                        `Swap failed and this is the last buffered swap (${fullyQualifiedSectionName})`
                    );
                } else {
                    this._logger.debug(
                        {
                            fullyQualifiedSectionName: fullyQualifiedSectionName,
                            swapsInProgress: swapsInProgress,
                        },
                        `Swap Section failed but others started before this one finished (${fullyQualifiedSectionName})`
                    );
                }
                //Lets tell the user this swap FAILED
                let errDeets = {
                    notDismissable: true,
                };
                if (error) {
                    if (error.hasOwnProperty("errorCode")) {
                        errDeets.error = error;
                    } else if (error.hasOwnProperty("message")) {
                        errDeets.errorTitle = "Error Encountered";
                        errDeets.errorCode = "ClientJsError";
                        errDeets.errorMsg = error.message;
                    }
                }

                //Provide the User with Feedback that this swap failed
                this._userFeedbackComponent.displayError(errDeets).then(
                    () => {
                        return getTheResultsOfThisSwapAndAllBeforeIt();
                    },
                    (e) => {
                        this._logger.fatal(
                            {fullyQualifiedSectionName: fullyQualifiedSectionName, error: e},
                            `Swap Section failed and we failed to display Error feedback to the user (${fullyQualifiedSectionName})`
                        );
                        return getTheResultsOfThisSwapAndAllBeforeIt();
                    }
                );
            }
        );
    }

    _onRouteInfoChanged(rollupData, fullRouteDetails) {
        //jshint ignore:line
        let originator = _.get(fullRouteDetails, "sourceDetails.originator");
        if (originator && originator === this) {
            this._logger.trace(
                {},
                "Route Details changed but this class was the originator so we are ignoring the change."
            );
            return Promise.resolve(NOTHING_HAPPENED_RESULTS);
        } else {
            let fullyQualifiedSectionName = this._getFqsnFromRouteDetails(
                fullRouteDetails.newRouteDetails
            );
            return this._changeSection(fullyQualifiedSectionName);
        }
    }

    _onRouteRefreshed(routeDetails) {
        let fullyQualifiedSectionName = this._getFqsnFromRouteDetails(routeDetails);
        return this._changeSection(fullyQualifiedSectionName);
    }

    _onSteveStartupCompleted(launchOpts) {
        this.verifyCurrentRouteAndSection(_.get(launchOpts, "defaultRoute"));
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/
    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */

    /*******************************************/
    get currentSectionDetails() {
        return this.getCurrentSectionDetails();
    }

    getCurrentSectionDetails() {
        return this._currentSectionDetails;
    }

    get nextSectionDetails() {
        return this.getNextSectionDetails();
    }

    getNextSectionDetails() {
        return this._nextSectionDetails;
    }

    //The Router may or may not be completed
    whenRouteChangesComplete() {
        if (this._routeChangesInProgressPromise) {
            return new Promise((resolve) => {
                this.once("SectionChangeCompleted", (results) => {
                    resolve(results);
                });
            });
        } else {
            return Promise.resolve();
        }
    }

    /**
     * Refreshes the Section or goes to the default route.
     * @throws {Error} - If the shutdown process has already begun.
     */
    verifyCurrentRouteAndSection(defaultRoute) {
        if (this._shutdownInProgress) {
            throw new Error("Shutdown is in Progress...");
        }
        let fullyQualifiedSectionName = this._getFqsnFromRouteDetails(this._router.routeDetails);
        if (fullyQualifiedSectionName) {
            this._changeSection(fullyQualifiedSectionName);
        } else if (defaultRoute) {
            this._router.updateRouteInfo(defaultRoute);
        } else {
            let err = {
                errorTitle: "No Section Provided",
                errorCode: "NoSectionProvided",
                errorMsg:
                    "We don't know where to take you as the url has no section specified and there is no default route defined, sorry.",
            };
            this._userFeedbackComponent.displayError(err);
            this._logger.fatal(
                {
                    err: err,
                    routeDetails: this._router.routeDetails,
                    defaultRoute: defaultRoute,
                },
                "No section specified in the url and no default route... Giving up sorry."
            );
        }
    }

    /**
     * Go To another section within the application, and be told when the task is complete.
     * @param {string} sectionName - The name of the section to navigate to.
     * @param {object} opts - the navigation options
     * @param {string} app - The name of the application to navigate to.
     */
    goTo(sectionName, opts) {
        let routeInfo = {
            s: sectionName,
        };
        let appName = _.get(opts, "app");
        if (appName) {
            routeInfo.app = appName;
        }
        //Note this should happen immediately no promise based stuff required.
        this._router.updateRouteInfo(routeInfo, {originator: this});
        let fullyQualifiedSectionName = this._getFqsnFromRouteDetails(this._router.routeDetails);

        return this._changeSection(fullyQualifiedSectionName);
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /* START STEVE ADD-ON METHODS */

    /*******************************************/
    initialize(/*steve, addOnOptions*/) {
        document.body.appendChild(this.renderer.rootElement);
    }

    startUp(/*steve, addOnOptions*/) {}

    /*******************************************/
    /* END STEVE ADD-ON METHODS */
    /*******************************************/
}

module.exports = CoreSectionRouterComponent;
CoreSectionRouterComponent.$inject = ["$teve.core", "$teve.logger", "$teve.di", "$teve.router"];
